import React from 'react';
import { LegalQuickRibbonBadge } from './LegalQuickRibbonBadge';

/** Fits badge label inside the icon width (uses --badge-chars in CSS). */
function quickTileBadgeStyle(label: string): React.CSSProperties {
  const chars = Math.max(1, label.trim().length);
  return { '--badge-chars': String(chars) } as React.CSSProperties;
}

export interface QuickServiceTileItem {
  categoryId: string;
  slug: string;
  title: string;
  badge?: string;
  badgeType?: 'demand' | 'free' | 'new';
  icon: string;
}

export interface QuickServiceTileSectionProps {
  id: string;
  title: React.ReactNode | null;
  items: QuickServiceTileItem[];
  onSelect: (categoryId: string, slug: string) => void;
  variant?: string;       // e.g. "towing" → adds class "legal-quick-section--towing"
  labelInside?: boolean;  // renders label inside the icon box (horizontal layout)
  footer?: React.ReactNode;
}

export const QuickServiceTileSection: React.FC<QuickServiceTileSectionProps> = ({
  id,
  title,
  items,
  onSelect,
  variant,
  labelInside = false,
  footer,
}) => {
  if (items.length === 0) return null;

  const sectionClass = ['legal-quick-section', variant ? `legal-quick-section--${variant}` : '']
    .filter(Boolean)
    .join(' ');

  return (
    <section
      id={`home-section-${id}`}
      className={sectionClass}
      aria-labelledby={title ? `quick-section-title-${id}` : undefined}
    >
      {title ? (
        <h2 id={`quick-section-title-${id}`} className="legal-quick-section__title">
          {title}
        </h2>
      ) : null}

      <ul className="legal-quick-tiles" role="list">
        {items.map((item) => (
          <li key={`${item.categoryId}-${item.slug}`} className="legal-quick-tiles__item">
            <button
              type="button"
              className="legal-quick-tile"
              onClick={() => onSelect(item.categoryId, item.slug)}
            >
              <span className="legal-quick-tile__media">
                <span className={`legal-quick-tile__icon-wrap${labelInside ? ' legal-quick-tile__icon-wrap--label-inside' : ''}`}>
                  <i className={`ti ${item.icon}`} aria-hidden />
                  {labelInside && (
                    <span className="legal-quick-tile__label-inside">{item.title}</span>
                  )}
                  {item.badge && item.badgeType === 'free' ? (
                    <LegalQuickRibbonBadge label={item.badge} />
                  ) : item.badge ? (
                    <span
                      className={[
                        'legal-quick-tile__badge',
                        item.badgeType ? `legal-quick-tile__badge--${item.badgeType}` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={quickTileBadgeStyle(item.badge)}
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </span>
              </span>
              {/* Only show external label when NOT in labelInside mode */}
              {!labelInside && (
                <span className="legal-quick-tile__label">{item.title}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {footer}
    </section>
  );
};
